import React from 'react';
import { Button, Tooltip } from '@mui/material';
import { useGridApiContext, useGridSelector, gridExpandedSortedRowEntriesSelector } from '@mui/x-data-grid-pro';
import { Print } from '@mui/icons-material';

export interface GridToolbarPrintProps {
    sx?: any;
    endIcon?: React.ReactNode;
}

export const GridToolbarPrint = ({ sx, endIcon }: GridToolbarPrintProps) => {
    const apiRef = useGridApiContext();
    const visibleRows = useGridSelector(apiRef, gridExpandedSortedRowEntriesSelector);

    const handlePrint = () => {
        try {
            const rows = visibleRows.map((rowEntry) => rowEntry.model).filter((row: any) => row !== null);
            const allColumns = apiRef.current.getVisibleColumns();
            const gridContainer = apiRef.current.rootElementRef?.current;
            if (!gridContainer) {
                console.error('Grid container not found');
                return;
            }

            const visibleColumns = allColumns.filter((col) => {
                if (['__check__', 'actions', '__detail_panel_toggle__'].includes(col.field)) {
                    return false;
                }

                const columnElement = gridContainer.querySelector(`[data-field="${col.field}"]`);
                if (!columnElement) return false;

                const rect = columnElement.getBoundingClientRect();
                const containerRect = gridContainer.getBoundingClientRect();

                return rect.left >= containerRect.left && rect.right <= containerRect.right;
            });

            const filteredColumns = visibleColumns;

            const printContent = createCustomPrintContent(rows, filteredColumns);

            const popupWidth = 800;
            const popupHeight = 600;
            const left = (window.screen.width - popupWidth) / 2;
            const top = (window.screen.height - popupHeight) / 2;

            const printWindow = window.open(
                '',
                'printPopup',
                `width=${popupWidth},height=${popupHeight},left=${left},top=${top},scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no,status=no`
            );

            if (printWindow) {
                printWindow.document.write(printContent);
                printWindow.document.close();

                printWindow.onload = () => {
                    printWindow.focus();
                    printWindow.print();

                    setTimeout(() => {
                        printWindow.close();
                    }, 1000);
                };
            }
        } catch (error) {
            console.error('Custom print error:', error);
            window.print();
        }
    };

    const createCustomPrintContent = (rows: any[], columns: any[]) => {
        const headers = columns
            .map((col) => {
                const headerText = col.headerName || col.field;
                const escapedHeader = escapeHtml(headerText);
                return `<th>${escapedHeader}</th>`;
            })
            .join('');

        const tableRows = rows
            .map((row) => {
                const cells = columns
                    .map((col) => {
                        const value = row[col.field];
                        const displayValue = formatCellValue(value, col.type);
                        const escapedValue = escapeHtml(displayValue);
                        return `<td>${escapedValue}</td>`;
                    })
                    .join('');
                return `<tr>${cells}</tr>`;
            })
            .join('');

        const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="utf-8">
                <title>Print</title>
                <style>
                    ${getPrintStyles()}
                </style>
            </head>
            <body>
                <div class="print-container">
                    <div class="print-content">
                        <table class="print-table">
                            <thead>
                                <tr>${headers}</tr>
                            </thead>
                            <tbody>
                                ${tableRows}
                            </tbody>
                        </table>
                    </div>
                </div>
            </body>
            </html>
        `;

        return htmlContent;
    };

    const escapeHtml = (text: string): string => {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    };

    const formatCellValue = (value: any, type?: string): string => {
        if (value === null || value === undefined) return '';

        if (Array.isArray(value)) {
            return value.map((item) => formatCellValue(item, type)).join(', ');
        }

        if (typeof value === 'object' && value !== null) {
            if (value.name) return value.name;
            if (value.label) return value.label;
            if (value.value) return value.value;
            if (value.id && value.name) return `${value.name} (${value.id})`;
            if (value.title) return value.title;
            if (value.text) return value.text;

            try {
                return JSON.stringify(value);
            } catch {
                return '[Complex Object]';
            }
        }

        switch (type) {
            case 'date':
                try {
                    return new Date(value).toLocaleDateString();
                } catch {
                    return String(value);
                }
            case 'dateTime':
                try {
                    return new Date(value).toLocaleString();
                } catch {
                    return String(value);
                }
            case 'number':
                return typeof value === 'number' ? value.toString() : String(value);
            case 'boolean':
                return value ? 'Yes' : 'No';
            case 'string':
                return String(value);
            default:
                return String(value);
        }
    };

    const getPrintStyles = () => `
        @media print {
            * {
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
            }
            
            body {
                font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif;
                font-size: 12px;
                line-height: 1.4;
                margin: 0;
                padding: 16px;
                color: rgba(0, 0, 0, 0.87);
                background: #fff;
            }
            
            .print-table {
                width: 100%;
                border-collapse: collapse;
                margin: 0;
                table-layout: auto;
                box-shadow: 0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12);
                border-radius: 4px;
                overflow: hidden;
            }
            
            .print-table th,
            .print-table td {
                border: 1px solid rgba(224, 224, 224, 1);
                padding: 8px 12px;
                text-align: left;
                font-size: 12px;
                line-height: 1.4;
                vertical-align: top;
                word-wrap: break-word;
                max-width: 200px;
            }
            
            .print-table th {
                background-color: rgba(0, 0, 0, 0.04);
                font-weight: 500;
                color: rgba(0, 0, 0, 0.87);
                border-bottom: 2px solid rgba(0, 0, 0, 0.12);
            }
            
            .print-table tbody tr:nth-child(even) {
                background-color: rgba(0, 0, 0, 0.02);
            }
            
            .print-table tbody tr:hover {
                background-color: rgba(0, 0, 0, 0.04);
            }
            
            @page {
                margin: 0.75in;
                size: A4 landscape;
            }
        }
        
        @media screen {
            body {
                font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif;
                margin: 0;
                padding: 24px;
                background-color: #fafafa;
                color: rgba(0, 0, 0, 0.87);
            }
            
            .print-table {
                width: 100%;
                border-collapse: collapse;
                margin: 0;
                table-layout: auto;
                background: #fff;
                border-radius: 4px;
                overflow: hidden;
                box-shadow: 0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12);
            }
            
            .print-table th,
            .print-table td {
                border: 1px solid rgba(224, 224, 224, 1);
                padding: 12px 16px;
                text-align: left;
                font-size: 14px;
                line-height: 1.4;
                vertical-align: top;
                word-wrap: break-word;
                max-width: 300px;
            }
            
            .print-table th {
                background-color: rgba(0, 0, 0, 0.04);
                font-weight: 500;
                color: rgba(0, 0, 0, 0.87);
                border-bottom: 2px solid rgba(0, 0, 0, 0.12);
            }
            
            .print-table tbody tr:nth-child(even) {
                background-color: rgba(0, 0, 0, 0.02);
            }
            
            .print-table tbody tr:hover {
                background-color: rgba(0, 0, 0, 0.04);
            }
        }
    `;

    return (
        <Tooltip title="Print">
            <Button sx={sx} onClick={handlePrint} startIcon={<Print />} endIcon={endIcon}>
                Print
            </Button>
        </Tooltip>
    );
};
